import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText,
  Package,
  Activity,
  Database,
  Sparkles,
  ShoppingBag,
  ArrowUpRight,
  ArrowRight,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config';
import { scopedKey, getActiveAccountId } from '../utils/accountSession';

const navCards = [
  {
    id: 'ahri-dashboard',
    title: 'AHRI Dashboard',
    description: 'View AHRI sync, mapping, inventory, and data-quality status.',
    to: '/ahri/dashboard',
    icon: Database,
    tone: 'primary',
  },
  {
    id: 'ahri-data',
    title: 'AHRI Data',
    description: 'Manage AHRI imports, mappings, and data sync.',
    to: '/ahri',
    icon: Database,
    tone: 'primary',
  },
  {
    id: 'ahri-system-shop',
    title: 'AHRI System Shop',
    description: 'Browse and configure AHRI system shop data.',
    to: '/ahri/shop',
    icon: ShoppingBag,
    tone: 'info',
  },
  {
    id: 'semantix-search',
    title: 'SemantiX Search',
    description: 'Search server settings and schema editor.',
    to: '/semantix',
    icon: Sparkles,
    tone: 'violet',
  },
  {
    id: 'products',
    title: 'Products',
    description: 'Manage products, attributes, and associations.',
    to: '/products',
    icon: Package,
    tone: 'success',
  },
  {
    id: 'data-quality',
    title: 'Data Quality',
    description: 'Review quality checks and cleanup tasks.',
    to: '/data-quality',
    icon: Activity,
    tone: 'warning',
  },
  {
    id: 'ai-virtual-tryout-room',
    title: 'AI Virtual Tryout Room',
    description: 'Launch virtual tryout experiences.',
    to: '/ai-tryout-studio/try-v2',
    icon: Sparkles,
    tone: 'violet',
  },
  {
    id: 'ai-documents',
    title: 'AI Documents',
    description: 'Open the Documents V4 experience.',
    to: '/v4/documents/list',
    icon: FileText,
    tone: 'primary',
  },
];

const TONE_STYLES = {
  primary: { bg: '#eff6ff', fg: '#2563eb', ring: 'rgba(37, 99, 235, 0.16)' },
  info: { bg: '#ecfeff', fg: '#0891b2', ring: 'rgba(8, 145, 178, 0.16)' },
  success: { bg: '#f0fdf4', fg: '#16a34a', ring: 'rgba(22, 163, 74, 0.16)' },
  warning: { bg: '#fffbeb', fg: '#d97706', ring: 'rgba(217, 119, 6, 0.16)' },
  violet: { bg: '#f5f3ff', fg: '#7c3aed', ring: 'rgba(124, 58, 237, 0.16)' },
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

const Dashboard = () => {
  const { getAuthHeaders, account } = useAuth();
  const [hiddenDashboardCardIds, setHiddenDashboardCardIds] = useState([]);

  useEffect(() => {
    let mounted = true;
    try {
      const cached = JSON.parse(localStorage.getItem(scopedKey('ui_preferences', getActiveAccountId())) || '{}');
      if (Array.isArray(cached?.hidden_dashboard_card_ids)) {
        setHiddenDashboardCardIds(cached.hidden_dashboard_card_ids);
      }
    } catch (err) {
      // ignore parse issues
    }
    const fetchUiPreferences = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/config/general`, {
          headers: getAuthHeaders?.() || {},
        });
        if (!response.ok) return;
        const data = await response.json();
        const hidden = data?.config?.ui_preferences?.hidden_dashboard_card_ids;
        if (!mounted) return;
        setHiddenDashboardCardIds(Array.isArray(hidden) ? hidden : []);
      } catch (err) {
        // keep defaults when settings are unavailable
      }
    };
    fetchUiPreferences();
    const onUiPreferenceUpdate = (event) => {
      const hidden = event?.detail?.hidden_dashboard_card_ids;
      if (Array.isArray(hidden)) {
        setHiddenDashboardCardIds(hidden);
      }
    };
    window.addEventListener('ui-preferences-updated', onUiPreferenceUpdate);
    return () => {
      mounted = false;
      window.removeEventListener('ui-preferences-updated', onUiPreferenceUpdate);
    };
  }, [getAuthHeaders]);

  const visibleNavCards = useMemo(
    () => navCards.filter((card) => !hiddenDashboardCardIds.includes(card.id)),
    [hiddenDashboardCardIds]
  );

  const firstName = (account?.name || account?.email || '').split(/[\s@]/)[0];

  return (
    <div className="dash-page">
      <div className="dash-hero">
        <div className="dash-hero-text">
          <span className="dash-eyebrow">
            <Sparkles size={13} /> AI Tech Assistant
          </span>
          <h1 className="dash-title">
            {getGreeting()}{firstName ? `, ${firstName}` : ''}
          </h1>
          <p className="dash-subtitle">
            Jump into the tools you use most — AHRI data, products, and AI workflows, all in one place.
          </p>
        </div>
        <div className="dash-hero-actions">
          <Link to="/ahri" className="dash-btn dash-btn-primary">
            Open AHRI Data
            <ArrowRight size={16} />
          </Link>
          <Link to="/v4/documents/list" className="dash-btn dash-btn-ghost">
            Open AI Documents
          </Link>
        </div>
      </div>

      <div className="dash-grid">
        {visibleNavCards.map((card) => {
          const Icon = card.icon;
          const tone = TONE_STYLES[card.tone] || TONE_STYLES.primary;
          return (
            <Link key={card.id} to={card.to} className="dash-card">
              <div
                className="dash-card-icon"
                style={{ background: tone.bg, color: tone.fg, boxShadow: `0 0 0 6px ${tone.ring}` }}
              >
                <Icon size={20} />
              </div>
              <div className="dash-card-body">
                <h3 className="dash-card-title">{card.title}</h3>
                <p className="dash-card-desc">{card.description}</p>
              </div>
              <div className="dash-card-footer" style={{ color: tone.fg }}>
                Open
                <ArrowUpRight size={15} />
              </div>
            </Link>
          );
        })}
      </div>

      <style>{`
        .dash-page {
          padding: 0 0 2rem;
        }

        .dash-hero {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: space-between;
          gap: 1.5rem;
          padding: 2rem 2.25rem;
          margin-bottom: 1.75rem;
          border-radius: 20px;
          background: linear-gradient(135deg, #eff6ff 0%, #e0e7ff 55%, #dbeafe 100%);
          border: 1px solid #e2e8f0;
          position: relative;
          overflow: hidden;
        }

        .dash-hero::after {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(540px 220px at 85% -10%, rgba(37, 99, 235, 0.1), transparent 70%);
          pointer-events: none;
        }

        .dash-hero-text {
          position: relative;
          max-width: 640px;
        }

        .dash-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 0.75rem;
          font-weight: 600;
          letter-spacing: 0.02em;
          text-transform: uppercase;
          color: #1d4ed8;
          background: rgba(255, 255, 255, 0.7);
          border: 1px solid rgba(37, 99, 235, 0.18);
          padding: 0.3rem 0.7rem;
          border-radius: 999px;
          margin-bottom: 1rem;
        }

        .dash-title {
          font-size: 1.85rem;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 0.6rem;
          letter-spacing: -0.01em;
        }

        .dash-subtitle {
          font-size: 0.975rem;
          color: #475569;
          margin: 0;
          line-height: 1.5;
        }

        .dash-hero-actions {
          position: relative;
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .dash-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 0.65rem 1.1rem;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.9rem;
          text-decoration: none;
          white-space: nowrap;
          transition: transform 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
        }

        .dash-btn-primary {
          background: #2563eb;
          color: #ffffff;
        }

        .dash-btn-primary:hover {
          transform: translateY(-1px);
          box-shadow: 0 8px 18px rgba(37, 99, 235, 0.25);
          background: #1d4ed8;
          color: #ffffff;
        }

        .dash-btn-ghost {
          background: rgba(255, 255, 255, 0.7);
          color: #1e3a8a;
          border: 1px solid rgba(37, 99, 235, 0.22);
        }

        .dash-btn-ghost:hover {
          background: #ffffff;
          color: #1e3a8a;
        }

        .dash-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
          gap: 1.1rem;
        }

        .dash-card {
          display: flex;
          flex-direction: column;
          gap: 0.9rem;
          padding: 1.4rem;
          background: #ffffff;
          border: 1px solid #eef1f6;
          border-radius: 16px;
          text-decoration: none;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
          transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
        }

        .dash-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 16px 30px rgba(15, 23, 42, 0.1);
          border-color: #e0e7ff;
        }

        .dash-card-icon {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .dash-card-title {
          font-size: 1.02rem;
          font-weight: 600;
          color: #0f172a;
          margin: 0 0 0.35rem;
        }

        .dash-card-desc {
          font-size: 0.875rem;
          color: #64748b;
          line-height: 1.5;
          margin: 0;
        }

        .dash-card-footer {
          margin-top: auto;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 0.85rem;
          font-weight: 600;
        }

        @media (max-width: 575.98px) {
          .dash-hero {
            padding: 1.5rem;
          }

          .dash-title {
            font-size: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
